import React, { useEffect, useId, useRef, useState } from 'react';
import templateApi from '../../../api/templateApi';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isTemplateId(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

const STATUS_LABELS = {
  template: 'Draft',
  ready: 'Published',
  sent: 'Sent',
  archived: 'Archived',
};

function statusLabel(status) {
  return STATUS_LABELS[status] || status;
}

export default function TemplateAutocomplete({
  value,
  onChange,
  excludeId,
  type,
  placeholder = 'Search templates…',
  emptyLabel = 'No templates found',
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const templateId = isTemplateId(value) ? value.trim() : '';
  const selected = options.find((item) => String(item.id) === templateId) || null;
  const displayValue = open ? query : (selected?.name || '');

  useEffect(() => {
    if (!templateId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const items = await templateApi.listPickerOptions({
          ...(type ? { type } : {}),
          highlight: templateId,
        });
        if (!cancelled) {
          setOptions((prev) => {
            const merged = [...items];
            prev.forEach((item) => {
              if (!merged.some((entry) => entry.id === item.id)) merged.push(item);
            });
            return merged;
          });
        }
      } catch (err) {
        console.warn('Failed to resolve selected template', err);
      }
    })();
    return () => { cancelled = true; };
  }, [templateId, type]);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await templateApi.listPickerOptions({
          search: open ? query : '',
          ...(type ? { type } : {}),
          exclude: excludeId,
          highlight: templateId || undefined,
        });
        if (!cancelled) {
          setOptions(items);
          setActiveIndex(-1);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to load template options', err);
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, open ? 250 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open, excludeId, type, templateId]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const pick = (item) => {
    onChange(String(item.id));
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((idx) => Math.min(idx + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((idx) => Math.max(idx - 1, 0));
    } else if (event.key === 'Enter' && activeIndex >= 0 && options[activeIndex]) {
      event.preventDefault();
      pick(options[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div className="pixie-autocomplete" ref={rootRef}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={displayValue}
        placeholder={loading && !displayValue ? 'Loading templates…' : placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
      />
      {open && (
        <ul id={listId} className="pixie-autocomplete-list" role="listbox">
          {loading && options.length === 0 && (
            <li className="pixie-autocomplete-empty">Loading…</li>
          )}
          {!loading && options.length === 0 && (
            <li className="pixie-autocomplete-empty">{emptyLabel}</li>
          )}
          {options.map((item, index) => (
            <li
              key={item.id}
              role="option"
              aria-selected={String(item.id) === String(templateId)}
              className={[
                'pixie-autocomplete-option',
                index === activeIndex ? 'is-active' : '',
                String(item.id) === String(templateId) ? 'is-selected' : '',
              ].filter(Boolean).join(' ')}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(item);
              }}
            >
              <span className="pixie-autocomplete-name">{item.name}</span>
              <span className={`pixie-autocomplete-status is-${item.status}`}>
                {statusLabel(item.status)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
